import React, { useMemo } from 'react'
import { useTbViewStore, useProjectStore, useAppEx } from '@vt/hooks'
import { pick, flatten, get } from 'lodash-es'
import styled from '@emotion/styled'
import { showNotification } from '@mantine/notifications'
import { Button, Modal } from '@ow/comps'
import { useDataOperateStore } from '../data-operate/data-operate-store-provider'
import { showDetail } from '../details-copy'

const downloadFile = function (url) {
  const iframe = document.createElement('a')
  iframe.style.display = 'none'
  iframe.style.height = 0
  iframe.style.pointerEvents = 'none'
  iframe.href = url
  document.body.appendChild(iframe)
  iframe.click()
  setTimeout(() => {
    iframe.remove()
  }, 5 * 60 * 1000)
}

const OptItem = styled.div`
  color: var(--ow-color-gray-100);
  &:hover {
    background: var(--ow-color-primary-50);
    color: var(--ow-color-primary-700);
  }
`

const maps = {
  logConfig: '查看日志',
  detailConfig: '详情',
  editorConfig: '编辑',
  batchEditorConfig: '编辑',
  recoveryConfig: '恢复',
  deleteConfig: '删除',
  downloadConfig: '下载文件',
}

const Operate = function (props) {
  const { payload, value = {}, rowIndex } = props
  const { allowEditor } = payload || {}

  const { rowData, defs } = value
  const { userInfoInApp } = useAppEx((state) => pick(state, ['userInfoInApp']))

  const {
    ditto,
    viewType,
    showRecycle,
    operatorConfig,
    editorConfig,
    recycleConfig,
    destroyTbData,
    recoverTbData,
    setRefreshStatus,
  } = useTbViewStore((state) =>
    pick(state, [
      'ditto',
      'viewType',
      'showRecycle',
      'editorConfig',
      'operatorConfig',
      'recycleConfig',
      'destroyTbData',
      'recoverTbData',
      'setRefreshStatus',
    ])
  )

  const customCompsMap = useProjectStore((state) => state.customCompsMap)
  const { operators } = useDataOperateStore((state) =>
    pick(state, ['operators'])
  )
  const { showCreate, showTbDetail, showLog } = operators || {}

  const { OperateComponent } = customCompsMap || {}

  const editTbData = async (data, updateData, extProps) => {
    const originalData = await ditto.getById(data.objectId)
    if (originalData) {
      showCreate({ ...originalData, ...updateData }, extProps)
    } else {
      showCreate(data || {}, extProps)
    }
  }

  // 查看详情
  const showCurrentDataDetail = (_data) => {
    showDetail({ currentData: _data })
  }

  const onCloumnMatrixOperate = (optType) => {
    const { objectId } = payload || {}
    if (optType === 'editorConfig') {
      return editTbData({ ...payload, objectId })
    }
    if (optType === 'logConfig') {
      return showLog({ objectId })
    }
    if (optType === 'recoveryConfig') {
      return recoverTbData(objectId).then(() => {
        showNotification({
          title: '操作成功',
        })
      })
    }
    if (optType === 'deleteConfig') {
      return destroyTbData([objectId])
    }
    // if (optType === 'detailConfig') {
    //   return showTbDetail({ ...payload, objectId }, true)
    // }
    return null
  }

  // 针对当前rowData允许的操作项
  const operatorsAllowed = useMemo(() => {
    const currentOperatorConfig = showRecycle
      ? operatorConfig.recycle
      : operatorConfig.operatorConfig
    const { allow, allowType } = editorConfig || {}

    if (!showRecycle) {
      const opts = get(currentOperatorConfig, 'elements', []).map((item) => {
        // 先只判断了编辑
        if (item.actionType === 'editorConfig') {
          if (
            rowData &&
            rowData.createdBy &&
            allow &&
            allowType === 'createBySelf' &&
            userInfoInApp.id === rowData.createdBy.id
          ) {
            return item
          }
          if (allowType !== 'createBySelf') {
            return item
          }
          return null
        }
        return item
      })

      return opts.filter((v) => v)
    }

    return (currentOperatorConfig && currentOperatorConfig.elements) || []
  }, [showRecycle, rowData])

  if (!operatorsAllowed.length) {
    return null
  }

  const footer = ({ close, data, payload: _payload }) => {
    const { objectId } = _payload || {}
    return (
      <div className="flex justify-center items-center gap-2">
        <Button stroke onClick={close}>
          取消
        </Button>
        <Button
          type="primary"
          onClick={() => {
            destroyTbData([objectId || data.objectId]).then(() => {
              setRefreshStatus(true)
              showNotification({
                title: '删除成功',
              })
            })
          }}
        >
          确认
        </Button>
      </div>
    )
  }

  const deleteRender = (trigger) => {
    return (
      <Modal
        trigger={trigger}
        showCloseButton
        size="sm"
        title="提示"
        footer={({ close }) =>
          footer({
            close,
            data: rowData,
            payload,
          })
        }
      >
        <div className="text-white-400" style={{ fontSize: '14px' }}>
          <div>
            {recycleConfig && recycleConfig.allow
              ? '删除后记录将被移至回收站，您可以在回收站中恢复记录。'
              : '删除后无法恢复、是否确定删除？'}
          </div>
        </div>
      </Modal>
    )
  }

  if (viewType === 'matrix') {
    const { allowType } = editorConfig || {}
    return (
      <div className="p-2" style={{ fontSize: '14px' }}>
        {operatorsAllowed.map((item) => {
          if (item.actionType === 'deleteConfig') {
            return deleteRender(
              <OptItem
                key={item.actionType}
                onClick={() => {
                  onCloumnMatrixOperate(item.actionType, payload)
                }}
                className="p-2 cursor-pointer"
              >
                {maps[item.actionType]}
              </OptItem>
            )
          }
          if (
            !allowEditor &&
            allowType === 'selfBindArea' &&
            item.actionType === 'editorConfig'
          ) {
            return null
          }

          if (item.actionType === 'detailConfig') {
            return (
              <Button
                type="primary"
                opacityType="transparent"
                style={{
                  fontSize: '13px',
                  margin: '0 -4px',
                }}
                onClick={() => {
                  showCurrentDataDetail(payload)
                }}
              >
                {maps[item.actionType]}
              </Button>
            )
          }
          return (
            <OptItem
              key={item.actionType}
              onClick={() => {
                onCloumnMatrixOperate(item.actionType, payload)
              }}
              className="p-2 cursor-pointer"
            >
              {maps[item.actionType]}
            </OptItem>
          )
        })}
      </div>
    )
  }

  const DefaultComponent = (
    <div
      className="row-operate h-full flex flex-row justify-end"
      style={{
        visibility: 'hidden',
        // position: 'absolute',
        height: '100%',
        // right: 0,
        background: 'var(--ow-color-primary-50)',
      }}
    >
      {(operatorsAllowed || []).map((opt) => {
        switch (opt.actionType) {
          case 'detailConfig':
            return (
              <Button
                type="primary"
                opacityType="transparent"
                style={{
                  fontSize: '13px',
                  margin: '0 -4px',
                }}
                onClick={() => {
                  showCurrentDataDetail(rowData)
                }}
              >
                {maps[opt.actionType]}
              </Button>
            )
          case 'editorConfig':
            return (
              <Button
                type="primary"
                opacityType="transparent"
                key={`operate-defalut-component-${opt.actionType}`}
                style={{
                  fontSize: '13px',
                  margin: '0 -4px',
                }}
                onClick={() => {
                  showCreate(rowData)
                }}
              >
                {maps[opt.actionType]}
              </Button>
            )
          case 'batchEditorConfig':
            return (
              <div
                className="flex justify-center items-center cursor-pointer"
                key={`operate-defalut-component-${opt.actionType}`}
                style={{
                  color: 'var(--ow-color-primary-700)',
                  fontSize: '14px',
                }}
                onClick={() => {
                  showCreate(rowData, {}, true)
                }}
              >
                {maps[opt.actionType]}
              </div>
            )
          case 'recoveryConfig':
            return (
              <Button
                key={`operate-defalut-component-${opt.actionType}`}
                type="primary"
                opacityType="transparent"
                style={{
                  fontSize: '13px',
                  margin: '0 -4px',
                }}
                onClick={() => {
                  recoverTbData(rowData.objectId).then(() => {
                    setRefreshStatus(true)
                    showNotification({
                      title: '操作成功',
                    })
                  })
                }}
              >
                {maps[opt.actionType]}
              </Button>
            )
          case 'deleteConfig':
            return deleteRender(
              <Button
                key={`operate-defalut-component-${opt.actionType}`}
                type="error"
                opacityType="transparent"
                style={{
                  fontSize: '13px',
                  margin: '0 -4px',
                }}
              >
                {maps[opt.actionType]}
              </Button>
            )
          case 'downloadConfig':
            return (
              <Button
                key={`operate-defalut-component-${opt.actionType}`}
                type="primary"
                opacityType="transparent"
                style={{
                  fontSize: '13px',
                  margin: '0 -4px',
                }}
                onClick={() => {
                  if (opt.slugs && opt.slugs.length) {
                    const arr = flatten(
                      opt.slugs.map((v) => {
                        return rowData[v]
                      })
                    ).filter((v) => v)
                    arr.forEach((v) => {
                      const endPosition = v.src.indexOf('?Expires=')

                      downloadFile(
                        `${
                          endPosition === -1
                            ? v.src
                            : v.src.slice(0, endPosition)
                        }`
                      )
                    })
                  }
                }}
              >
                {maps[opt.actionType]}
              </Button>
            )
          default:
            return null
        }
      })}
    </div>
  )

  if (OperateComponent && !showRecycle) {
    return (
      <div
        className="w-full row-operate h-4/6 flex flex-row justify-end gap-2"
        style={{
          width: '100%',
          height: '100%',
          visibility: 'hidden',
          position: 'absolute',
          right: 0,
        }}
      >
        <OperateComponent
          rowIndex={rowIndex}
          rowData={rowData}
          defs={defs}
          onClickOpenDetail={showTbDetail}
          onClickUpdateData={showCreate}
          onClickDestroyData={() => {
            destroyTbData([rowData.objectId])
          }}
        >
          {operatorsAllowed && operatorsAllowed.length
            ? DefaultComponent
            : null}
        </OperateComponent>
      </div>
    )
  }

  return DefaultComponent
}
export default Operate
